import { Box, FormControl, FormHelperText } from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectProps } from "@mui/material/Select";
import clsx from "clsx";
import React, { ReactNode, useState } from "react";
import { ISelectOption } from "src/shared/types/field.types";

export type ISelectFieldProps<T = string> = SelectProps<T> & {
  options?: ISelectOption<T>[];
  helperText?: ReactNode;
  minWidth?: number | string;
  position?: "static" | "relative" | "absolute" | "sticky" | "fixed";
};

function SelectField<T = string>(
  {
    startAdornment,
    options,
    size = "small",
    label,
    fullWidth = true,
    children,
    helperText,
    minWidth = 0,
    position,
    MenuProps,
    ...selectProps
  }: ISelectFieldProps<T>,
  ref: React.ForwardedRef<HTMLSelectElement>,
) {
  const { onFocus, onBlur, value } = selectProps;

  const [isShrinkLable, setIsShrinkLable] = useState(Boolean(value));
  const [open, setOpen] = React.useState(false);
  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };
  const onInputFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (onFocus) {
      onFocus(e);
    }
    if (!e.target.value) {
      setIsShrinkLable(true);
    }
  };

  const onInputBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (onBlur) {
      onBlur(e);
    }
    if (!e.target.value) {
      setIsShrinkLable(false);
    }
  };

  const hasStartAdornment = !!startAdornment;

  const getLableShrink = (): boolean | undefined => {
    if (selectProps.displayEmpty) {
      if (value === 0 || value === null) {
        return true;
      }

      return Boolean(value);
    }

    if (hasStartAdornment) {
      return isShrinkLable;
    }

    return undefined;
  };

  const renderChildren = () => {
    if (!!options && options.length > 0) {
      return options.map(
        ({ name: menuItemLabel, value: menuItemValue, disabled }) => (
          <MenuItem
            key={`${menuItemLabel}-${String(menuItemValue)}`}
            value={menuItemValue as string | number}
            disabled={disabled}
            sx={{
              whiteSpace: "unset",
              display: "flex",
              alignItems: "center",
            }}
          >
            {menuItemLabel}
          </MenuItem>
        ),
      );
    }
    if (children) {
      return children;
    }
    if (value) {
      return (
        <MenuItem
          value={String(value)}
          sx={{
            whiteSpace: "unset",
            display: "flex",
            alignItems: "center",
          }}
        >
          {String(value)}
        </MenuItem>
      );
    }

    return [];
  };

  return (
    <FormControl
      fullWidth={fullWidth}
      size={size}
      error={selectProps.error}
      sx={{ minWidth, position }}
    >
      {label && (
        <InputLabel
          disabled={selectProps.disabled}
          size={size === "small" ? "small" : "normal"}
          shrink={getLableShrink()}
          className={clsx({ adornedStart: hasStartAdornment })}
        >
          {label}
        </InputLabel>
      )}
      <Select<T>
        size={size}
        ref={ref}
        open={open}
        onClose={handleClose}
        onOpen={handleOpen}
        onFocus={onInputFocus}
        onBlur={onInputBlur}
        label={label}
        startAdornment={
          hasStartAdornment ? (
            // otherwise select won't be opened on adornment click
            <Box sx={{ cursor: "pointer" }} onClick={handleOpen}>
              {startAdornment}
            </Box>
          ) : undefined
        }
        {...selectProps}
        MenuProps={{
          ...MenuProps,
          PaperProps: {
            sx: {
              boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.12)",
              maxHeight: 336,
              maxWidth: 300,
            },
          },
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: -8,
            horizontal: 0,
          },
        }}
      >
        {renderChildren()}
      </Select>
      {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}

export default React.forwardRef(SelectField) as <T>(
  props: ISelectFieldProps<T> & { ref?: React.ForwardedRef<HTMLSelectElement> },
) => ReturnType<typeof SelectField>;
